import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const ApiDocumentation = () => {
  const [activeCodeTab, setActiveCodeTab] = useState({
    query: 'curl',
    feedback: 'curl'
  });
  const [activeSidebarLink, setActiveSidebarLink] = useState('overview');

  // Smooth scrolling for sidebar links
  const handleSidebarClick = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSidebarLink(targetId);
    }
  };

  // Code example switching
  const showCodeExample = (prefix, language) => {
    setActiveCodeTab(prev => ({
      ...prev,
      [prefix]: language
    }));
  };

  // Intersection Observer for sidebar active states
  useEffect(() => {
    const sections = document.querySelectorAll('[id]');
    
    const observerOptions = {
      rootMargin: '-100px 0px -70% 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSidebarLink(entry.target.id);
        }
      });
    }, observerOptions);
    
    sections.forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Sidebar Navigation */
        .sidebar {
            position: fixed;
            left: 0;
            top: 70px;
            width: 250px;
            height: calc(100vh - 70px);
            background: rgba(255, 255, 255, 0.02);
            border-right: 1px solid rgba(255, 255, 255, 0.1);
            padding: 2rem 1rem;
            overflow-y: auto;
        }
        
        .sidebar-section {
            margin-bottom: 2rem;
        }
        
        .sidebar-title {
            font-size: 0.9rem;
            text-transform: uppercase;
            color: #667eea;
            font-weight: 600;
            margin-bottom: 0.5rem;
        }
        
        .sidebar-links {
            list-style: none;
        }
        
        .sidebar-links li {
            margin-bottom: 0.5rem;
        }
        
        .sidebar-links a {
            color: #888;
            text-decoration: none;
            display: block;
            padding: 0.5rem 1rem;
            border-radius: 8px;
            transition: all 0.3s;
        }
        
        .sidebar-links a:hover {
            background: rgba(255, 255, 255, 0.05);
            color: white;
        }
        
        .sidebar-links a.active {
            background: rgba(102, 126, 234, 0.2);
            color: #667eea;
        }
        
        /* Main Content */
        .main-content {
            margin-left: 250px;
            padding: 100px 3rem 3rem;
            max-width: 1200px;
        }
        
        /* Header */
        .page-header {
            margin-bottom: 3rem;
        }
        
        .page-title {
            font-size: 3rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.2rem;
            color: #888;
        }
        
        /* API Base Info */
        .api-base-info {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 3rem;
        }
        
        .base-url {
            font-family: monospace;
            font-size: 1.1rem;
            color: #667eea;
        }
        
        /* Endpoint Section */
        .endpoint-section {
            margin-bottom: 4rem;
        }
        
        .endpoint-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 2rem;
            transition: all 0.3s;
        }
        
        .endpoint-card:hover {
            border-color: rgba(102, 126, 234, 0.3);
            background: rgba(255, 255, 255, 0.05);
        }
        
        .endpoint-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        
        .method-badge {
            padding: 0.4rem 1rem;
            border-radius: 6px;
            font-weight: bold;
            font-size: 0.9rem;
            text-transform: uppercase;
        }
        
        .method-get { background: #48bb78; color: #0a0a0a; }
        .method-post { background: #667eea; color: white; }
        .method-put { background: #ed8936; color: white; }
        .method-delete { background: #f56565; color: white; }
        
        .endpoint-path {
            font-family: monospace;
            font-size: 1.2rem;
            flex: 1;
        }
        
        .endpoint-description {
            color: #bbb;
            margin-bottom: 1.5rem;
        }
        
        /* Parameters Table */
        .params-table {
            width: 100%;
            border-collapse: collapse;
            margin: 1.5rem 0;
        }
        
        .params-table th,
        .params-table td {
            text-align: left;
            padding: 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .params-table th {
            background: rgba(255, 255, 255, 0.05);
            font-weight: 600;
            color: #667eea;
        }
        
        .param-name {
            font-family: monospace;
            color: #c792ea;
        }
        
        .param-type {
            font-family: monospace;
            font-size: 0.9rem;
            color: #82aaff;
        }
        
        .param-required {
            color: #f78c6c;
            font-weight: bold;
        }
        
        .param-optional {
            color: #888;
        }
        
        /* Code Examples */
        .code-tabs {
            display: flex;
            gap: 1rem;
            margin-bottom: 1rem;
        }
        
        .code-tab {
            padding: 0.5rem 1.5rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px 8px 0 0;
            cursor: pointer;
            transition: all 0.3s;
        }
        
        .code-tab.active {
            background: #1a1a1a;
            border-bottom-color: #1a1a1a;
        }
        
        .code-container {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 0 12px 12px 12px;
            overflow: hidden;
            margin-bottom: 1.5rem;
        }
        
        .code-content {
            padding: 1.5rem;
            overflow-x: auto;
        }
        
        .code-content pre {
            margin: 0;
            color: #e0e0e0;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 0.9rem;
            line-height: 1.6;
        }
        
        /* Response Example */
        .response-section {
            margin-top: 2rem;
        }
        
        .response-status {
            display: inline-block;
            padding: 0.3rem 0.8rem;
            border-radius: 6px;
            font-weight: bold;
            font-size: 0.9rem;
            margin-bottom: 1rem;
        }
        
        .status-200 { background: #48bb78; color: #0a0a0a; }
        .status-400 { background: #ed8936; color: white; }
        .status-401 { background: #f56565; color: white; }
        .status-500 { background: #e53e3e; color: white; }
        
        /* Authentication Section */
        .auth-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 3rem;
        }
        
        .auth-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1rem;
        }
        
        .auth-icon {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
        }
        
        /* Rate Limiting */
        .rate-limit-info {
            background: rgba(237, 137, 54, 0.1);
            border: 1px solid rgba(237, 137, 54, 0.3);
            border-radius: 12px;
            padding: 1.5rem;
            margin: 2rem 0;
        }
        
        .rate-limit-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
        }
        
        .warning-icon {
            color: #ed8936;
            font-size: 1.2rem;
        }
        
        /* Try It Out Section */
        .try-it-section {
            background: rgba(102, 126, 234, 0.05);
            border: 2px solid rgba(102, 126, 234, 0.2);
            border-radius: 16px;
            padding: 2rem;
            margin-top: 2rem;
        }
        
        .try-button {
            display: inline-block;
            padding: 0.8rem 2rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .try-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
            .sidebar {
                transform: translateX(-100%);
                transition: transform 0.3s;
            }
            
            .sidebar.open {
                transform: translateX(0);
            }
            
            .main-content {
                margin-left: 0;
                padding: 100px 2rem 2rem;
            }
        }
        
        /* Code Syntax Highlighting */
        .keyword { color: #c792ea; }
        .string { color: #c3e88d; }
        .number { color: #f78c6c; }
        .comment { color: #676e95; }
        .function { color: #82aaff; }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>API Documentation</div>
        </div>
      </nav>
      
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-section">
          <div className="sidebar-title">Getting Started</div>
          <ul className="sidebar-links">
            <li>
              <a 
                href="#overview" 
                className={activeSidebarLink === 'overview' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'overview')}
              >
                Overview
              </a>
            </li>
            <li>
              <a 
                href="#authentication" 
                className={activeSidebarLink === 'authentication' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'authentication')}
              >
                Authentication
              </a>
            </li>
            <li>
              <a 
                href="#rate-limiting" 
                className={activeSidebarLink === 'rate-limiting' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'rate-limiting')}
              >
                Rate Limiting
              </a>
            </li>
          </ul>
        </div>
        
        <div className="sidebar-section">
          <div className="sidebar-title">Endpoints</div>
          <ul className="sidebar-links">
            <li>
              <a 
                href="#health" 
                className={activeSidebarLink === 'health' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'health')}
              >
                GET /health
              </a>
            </li>
            <li>
              <a 
                href="#query" 
                className={activeSidebarLink === 'query' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'query')}
              >
                POST /query
              </a>
            </li>
            <li>
              <a 
                href="#feedback" 
                className={activeSidebarLink === 'feedback' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'feedback')}
              >
                POST /feedback
              </a>
            </li>
            <li>
              <a 
                href="#stats" 
                className={activeSidebarLink === 'stats' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'stats')}
              >
                GET /stats
              </a>
            </li>
            <li>
              <a 
                href="#reindex" 
                className={activeSidebarLink === 'reindex' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'reindex')}
              >
                POST /reindex
              </a>
            </li>
          </ul>
        </div>
        
        <div className="sidebar-section">
          <div className="sidebar-title">Examples</div>
          <ul className="sidebar-links">
            <li>
              <a 
                href="#examples-curl" 
                className={activeSidebarLink === 'examples-curl' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'examples-curl')}
              >
                cURL
              </a>
            </li>
            <li>
              <a 
                href="#examples-python" 
                className={activeSidebarLink === 'examples-python' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'examples-python')}
              >
                Python
              </a>
            </li>
            <li>
              <a 
                href="#examples-js" 
                className={activeSidebarLink === 'examples-js' ? 'active' : ''}
                onClick={(e) => handleSidebarClick(e, 'examples-js')}
              >
                JavaScript
              </a>
            </li>
          </ul>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="main-content">
        {/* Header */}
        <div className="page-header" id="overview">
          <h1 className="page-title">API Documentation</h1>
          <p className="page-subtitle">Interface RESTful pour l'Agent RAG Hybride Banking</p>
        </div>
        
        {/* Base Info */}
        <div className="api-base-info">
          <h3>Base URL</h3>
          <p className="base-url">https://api.rag-banking.example.com/v1</p>
          <p style={{marginTop: '0.5rem', color: '#888'}}>Toutes les requêtes doivent inclure le header d'authentification</p>
        </div>
        
        {/* Authentication Section */}
        <div className="auth-section" id="authentication">
          <div className="auth-header">
            <div className="auth-icon">🔐</div>
            <div>
              <h2>Authentication</h2>
              <p style={{color: '#888'}}>Bearer Token Authentication</p>
            </div>
          </div>
          
          <p>L'API utilise l'authentification Bearer Token. Incluez votre token dans le header Authorization :</p>
          
          <div className="code-container" style={{marginTop: '1rem'}}>
            <div className="code-content">
              <pre>Authorization: Bearer YOUR_API_TOKEN</pre>
            </div>
          </div>
          
          <p>Pour obtenir un token d'API, contactez l'équipe d'administration.</p>
        </div>
        
        {/* Rate Limiting */}
        <div className="rate-limit-info" id="rate-limiting">
          <div className="rate-limit-header">
            <span className="warning-icon">⚠️</span>
            <h3>Rate Limiting</h3>
          </div>
          <p>L'API est limitée à <strong>1000 requêtes par heure</strong> par token.</p>
          <p>Les headers de réponse incluent :</p>
          <ul style={{marginTop: '0.5rem', paddingLeft: '2rem'}}>
            <li><code>X-RateLimit-Limit</code> : Limite totale</li>
            <li><code>X-RateLimit-Remaining</code> : Requêtes restantes</li>
            <li><code>X-RateLimit-Reset</code> : Timestamp de reset</li>
          </ul>
        </div>
        
        {/* Endpoints */}
        <div className="endpoint-section">
          <h2 style={{marginBottom: '2rem'}}>Endpoints</h2>
          
          {/* Health Check */}
          <div className="endpoint-card" id="health">
            <div className="endpoint-header">
              <span className="method-badge method-get">GET</span>
              <span className="endpoint-path">/health</span>
            </div>
            <p className="endpoint-description">Vérification de l'état de santé de l'API et de ses composants.</p>
            
            <div className="response-section">
              <div className="response-status status-200">200 OK</div>
              <div className="code-container">
                <div className="code-content">
                  <pre>{`{
  "status": "healthy",
  "timestamp": "2024-01-15T10:30:00Z",
  "agent_ready": true,
  "components": {
    "redis": "connected",
    "vector_db": "healthy",
    "llm": "available"
  }
}`}</pre>
                </div>
              </div>
            </div>
          </div>
          
          {/* Query Endpoint */}
          <div className="endpoint-card" id="query">
            <div className="endpoint-header">
              <span className="method-badge method-post">POST</span>
              <span className="endpoint-path">/query</span>
            </div>
            <p className="endpoint-description">Endpoint principal pour soumettre des questions à l'agent RAG.</p>
            
            <h4 style={{marginTop: '1.5rem'}}>Request Body</h4>
            <table className="params-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="param-name">question</span></td>
                  <td><span className="param-type">string</span></td>
                  <td><span className="param-required">Required</span></td>
                  <td>La question à poser à l'agent</td>
                </tr>
                <tr>
                  <td><span className="param-name">profile</span></td>
                  <td><span className="param-type">string</span></td>
                  <td><span className="param-optional">Optional</span></td>
                  <td>Profil utilisateur : IT, BackOffice, AMOA, MOE (défaut: BackOffice)</td>
                </tr>
                <tr>
                  <td><span className="param-name">use_cache</span></td>
                  <td><span className="param-type">boolean</span></td>
                  <td><span className="param-optional">Optional</span></td>
                  <td>Utiliser le cache pour des réponses plus rapides (défaut: true)</td>
                </tr>
                <tr>
                  <td><span className="param-name">filters</span></td>
                  <td><span className="param-type">object</span></td>
                  <td><span className="param-optional">Optional</span></td>
                  <td>Filtres additionnels (authority, date_range, etc.)</td>
                </tr>
              </tbody>
            </table>
            
            <h4>Example Request</h4>
            <div className="code-tabs">
              <div 
                className={`code-tab ${activeCodeTab.query === 'curl' ? 'active' : ''}`} 
                onClick={() => showCodeExample('query', 'curl')}
              >
                cURL
              </div>
              <div 
                className={`code-tab ${activeCodeTab.query === 'python' ? 'active' : ''}`} 
                onClick={() => showCodeExample('query', 'python')}
              >
                Python
              </div>
              <div 
                className={`code-tab ${activeCodeTab.query === 'js' ? 'active' : ''}`} 
                onClick={() => showCodeExample('query', 'js')}
              >
                JavaScript
              </div>
            </div>
            
            <div className="code-container" style={{display: activeCodeTab.query === 'curl' ? 'block' : 'none'}}>
              <div className="code-content">
                <pre>{`curl -X POST https://api.rag-banking.example.com/v1/query \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "question": "Comment traiter un chèque impayé selon BEAC?",
    "profile": "BackOffice",
    "use_cache": true
  }'`}</pre>
              </div>
            </div>
            
            <div className="code-container" style={{display: activeCodeTab.query === 'python' ? 'block' : 'none'}}>
              <div className="code-content">
                <pre><span className="keyword">import</span> requests

url = <span className="string">"https://api.rag-banking.example.com/v1/query"</span>
headers = {'{'}
    <span className="string">"Authorization"</span>: <span className="string">"Bearer YOUR_API_TOKEN"</span>,
    <span className="string">"Content-Type"</span>: <span className="string">"application/json"</span>
{'}'}
data = {'{'}
    <span className="string">"question"</span>: <span className="string">"Comment traiter un chèque impayé selon BEAC?"</span>,
    <span className="string">"profile"</span>: <span className="string">"BackOffice"</span>,
    <span className="string">"use_cache"</span>: <span className="keyword">True</span>
{'}'}

response = requests.post(url, json=data, headers=headers)
print(response.json())</pre>
              </div>
            </div>
            
            <div className="code-container" style={{display: activeCodeTab.query === 'js' ? 'block' : 'none'}}>
              <div className="code-content">
                <pre><span className="keyword">const</span> response = <span className="keyword">await</span> <span className="function">fetch</span>(<span className="string">'https://api.rag-banking.example.com/v1/query'</span>, {'{'}
  method: <span className="string">'POST'</span>,
  headers: {'{'}
    <span className="string">'Authorization'</span>: <span className="string">'Bearer YOUR_API_TOKEN'</span>,
    <span className="string">'Content-Type'</span>: <span className="string">'application/json'</span>
  {'}'},
  body: <span className="function">JSON.stringify</span>({'{'}
    question: <span className="string">'Comment traiter un chèque impayé selon BEAC?'</span>,
    profile: <span className="string">'BackOffice'</span>,
    use_cache: <span className="keyword">true</span>
  {'}'})
{'}'});

<span className="keyword">const</span> data = <span className="keyword">await</span> response.json();
console.log(data);</pre>
              </div>
            </div>
            
            <div className="response-section">
              <h4>Response</h4>
              <div className="response-status status-200">200 OK</div>
              <div className="code-container">
                <div className="code-content">
                  <pre>{`{
  "query_id": "q_1234567890",
  "question": "Comment traiter un chèque impayé selon BEAC?",
  "answer": "Pour traiter un chèque impayé selon les règles BEAC...",
  "confidence_score": 0.95,
  "sources": [
    {
      "title": "Procédure BEAC n°2023-15",
      "type": "regulation",
      "relevance": 0.98
    },
    {
      "title": "Module gub_cheque_reject.4gl",
      "type": "code",
      "relevance": 0.89
    }
  ],
  "search_methods_used": ["vector", "keyword", "graph"],
  "processing_time": 1.85,
  "api_version": "1.0"
}`}</pre>
                </div>
              </div>
            </div>
          </div>
          
          {/* Feedback Endpoint */}
          <div className="endpoint-card" id="feedback">
            <div className="endpoint-header">
              <span className="method-badge method-post">POST</span>
              <span className="endpoint-path">/feedback</span>
            </div>
            <p className="endpoint-description">Soumettre un feedback sur une réponse pour amélioration continue.</p>
            
            <h4>Request Body</h4>
            <table className="params-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="param-name">query_id</span></td>
                  <td><span className="param-type">string</span></td>
                  <td><span className="param-required">Required</span></td>
                  <td>ID de la requête concernée</td>
                </tr>
                <tr>
                  <td><span className="param-name">rating</span></td>
                  <td><span className="param-type">integer</span></td>
                  <td><span className="param-required">Required</span></td>
                  <td>Note de 1 à 5</td>
                </tr>
                <tr>
                  <td><span className="param-name">user_profile</span></td>
                  <td><span className="param-type">string</span></td>
                  <td><span className="param-required">Required</span></td>
                  <td>Profil de l'utilisateur</td>
                </tr>
                <tr>
                  <td><span className="param-name">comment</span></td>
                  <td><span className="param-type">string</span></td>
                  <td><span className="param-optional">Optional</span></td>
                  <td>Commentaire additionnel</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Stats Endpoint */}
          <div className="endpoint-card" id="stats">
            <div className="endpoint-header">
              <span className="method-badge method-get">GET</span>
              <span className="endpoint-path">/stats</span>
            </div>
            <p className="endpoint-description">Obtenir des statistiques d'utilisation de l'API.</p>
            
            <h4>Query Parameters</h4>
            <table className="params-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="param-name">days</span></td>
                  <td><span className="param-type">integer</span></td>
                  <td><span className="param-optional">Optional</span></td>
                  <td>Nombre de jours d'historique (défaut: 7)</td>
                </tr>
              </tbody>
            </table>
            
            <div className="response-section">
              <div className="response-status status-200">200 OK</div>
              <div className="code-container">
                <div className="code-content">
                  <pre>{`{
  "period": "7 days",
  "total_queries": 15234,
  "average_response_time": 1.92,
  "cache_hit_rate": 0.64,
  "queries_by_profile": {
    "IT": 4521,
    "BackOffice": 7832,
    "AMOA": 1890,
    "MOE": 991
  },
  "top_search_methods": {
    "vector": 0.85,
    "keyword": 0.72,
    "graph": 0.23,
    "metadata": 0.15
  }
}`}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Try It Out */}
        <div className="try-it-section">
          <h2>Essayer l'API</h2>
          <p>Testez l'API directement depuis votre navigateur avec notre interface interactive.</p>
          <a href="demo-playground.html" className="try-button" style={{marginTop: '1rem', display: 'inline-block'}}>
            Accéder au Playground →
          </a>
        </div>
      </main>
    </>
  );
};

export default ApiDocumentation;